import {
    Box,
    Drawer,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    CircularProgress,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    TextField,
    Divider,
    InputAdornment,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useState, useEffect, useMemo } from "react";
import { ICupom } from "../../interface/ICupom";
import { useCuponsPedido } from "../../hooks/useCuponsPedido";
import { TipoCupomDescricao } from "../../enum/TipoCupom";
import { TipoCupom } from "../../enum/TipoCupom";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { calcularDescontoCupomComCuponsSelecionados } from "../../utils/calcularDescontoCupomComCuponsSelecionados";
import { TipoMarketplace } from "../../../marketplace/enum/TipoMarketplace";

interface CuponsDrawerProps {
    aberto: boolean;
    fechar: () => void;
    cuponsAplicados: ICupom[];
    aplicarCupom: (cupom: ICupom) => Promise<void>;
    removerCupom: (codigo: string, tipo: string) => Promise<void>;
    pedidoUuid?: string;
    valorFrete?: number;
    valorTotalProdutos: number;
}

type TipoDescontoRapido = TipoCupom.TOTAL | TipoCupom.PORCENTAGEM;

export const CuponsDrawer = ({
    aberto,
    fechar,
    cuponsAplicados,
    aplicarCupom,
    removerCupom,
    valorFrete = 0,
    valorTotalProdutos,
}: CuponsDrawerProps) => {
    const [cuponsDisponiveis, setCuponsDisponiveis] = useState<ICupom[]>([]);
    const [cuponsSelecionados, setCuponsSelecionados] = useState<ICupom[]>([]);
    const [loadingConcluir, setLoadingConcluir] = useState(false);
    const [valorDesconto, setValorDesconto] = useState("");
    const [tipoDesconto, setTipoDesconto] = useState<TipoDescontoRapido>(TipoCupom.TOTAL);
    const { buscarCuponsValidos, loading } = useCuponsPedido();
    const { real } = useMoeda();

    useEffect(() => {
        if (aberto) {
            carregarCuponsDisponiveis();
            setCuponsSelecionados([...cuponsAplicados]);
            setValorDesconto("");
            setTipoDesconto(TipoCupom.TOTAL);
        }
    }, [aberto]);

    const carregarCuponsDisponiveis = async () => {
        const cupons = await buscarCuponsValidos();
        const cuponsNaoAplicados = cupons.filter(
            (cupom) =>
                !cuponsAplicados.some(
                    (aplicado) =>
                        aplicado.codigo === cupom.codigo &&
                        aplicado.tipo === cupom.tipo
                )
        );
        setCuponsDisponiveis(cuponsNaoAplicados);
    };

    type CupomComFlag = ICupom & { aplicado: boolean };

    const todosCupons = useMemo<CupomComFlag[]>(() => {
        const disponiveis: CupomComFlag[] = cuponsDisponiveis.map((cupom) => ({
            ...cupom,
            aplicado: false,
        }));
        const aplicados: CupomComFlag[] = cuponsAplicados.map((cupom) => ({
            ...cupom,
            aplicado: true,
        }));
        return [...disponiveis, ...aplicados];
    }, [cuponsDisponiveis, cuponsAplicados]);

    const temMudancas = useMemo(() => {
        const aplicarComparacao = (a: ICupom, b: ICupom) =>
            a.codigo === b.codigo && a.tipo === b.tipo;

        if (cuponsAplicados.length !== cuponsSelecionados.length) {
            return true;
        }

        const todosAplicadosEstaoSelecionados = cuponsAplicados.every((aplicado) =>
            cuponsSelecionados.some((selecionado) => aplicarComparacao(aplicado, selecionado))
        );

        const todosSelecionadosEstaoAplicados = cuponsSelecionados.every((selecionado) =>
            cuponsAplicados.some((aplicado) => aplicarComparacao(selecionado, aplicado))
        );

        return !todosAplicadosEstaoSelecionados || !todosSelecionadosEstaoAplicados;
    }, [cuponsAplicados, cuponsSelecionados]);

    const toggleCupomSelecionado = (cupom: CupomComFlag) => {
        setCuponsSelecionados((prev) => {
            const jaSelecionado = prev.some(
                (c) => c.codigo === cupom.codigo && c.tipo === cupom.tipo
            );
            if (jaSelecionado) {
                return prev.filter(
                    (c) => !(c.codigo === cupom.codigo && c.tipo === cupom.tipo)
                );
            } else {
                return [...prev, cupom];
            }
        });
    };

    const gerarCodigoUnico = () =>
        `DESC${Date.now().toString(36).toUpperCase()}`;

    const converterParaNumero = (valor: string): number => {
        if (!valor) return 0;
        const limpo = valor.replace(/\./g, "").replace(",", ".");
        return parseFloat(limpo) || 0;
    };

    const handleAplicarDesconto = async () => {
        const valorNumerico =
            tipoDesconto === TipoCupom.PORCENTAGEM
                ? parseFloat(valorDesconto) || 0
                : converterParaNumero(valorDesconto);

        if (valorNumerico <= 0) return;

        const codigo = gerarCodigoUnico();
        const cupom: ICupom = {
            codigo,
            tipo: tipoDesconto,
            valor: valorNumerico,
            nome:
                tipoDesconto === TipoCupom.PORCENTAGEM
                    ? `Desconto de ${valorNumerico}%`
                    : `Desconto de ${real(valorNumerico)}`,
            descricao: "Desconto manual",
            ativo: true,
            marketplaces: [{ tipo: TipoMarketplace.PDV }],
        };

        await aplicarCupom(cupom);
        fechar();
    };

    const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        if (tipoDesconto === TipoCupom.PORCENTAGEM) {
            const apenasNumeros = input.replace(/[^0-9.]/g, "");
            const numero = parseFloat(apenasNumeros);
            if (apenasNumeros === "" || apenasNumeros === "." || (numero >= 0 && numero <= 100)) {
                setValorDesconto(apenasNumeros);
            }
        } else {
            setValorDesconto(input);
        }
    };

    const handleConcluir = async () => {
        setLoadingConcluir(true);
        try {
            const cuponsParaAplicar = cuponsSelecionados.filter(
                (cupomSelecionado) =>
                    !cuponsAplicados.some(
                        (aplicado) =>
                            aplicado.codigo === cupomSelecionado.codigo &&
                            aplicado.tipo === cupomSelecionado.tipo
                    )
            );

            const cuponsParaRemover = cuponsAplicados.filter(
                (cupomAplicado) =>
                    !cuponsSelecionados.some(
                        (selecionado) =>
                            selecionado.codigo === cupomAplicado.codigo &&
                            selecionado.tipo === cupomAplicado.tipo
                    )
            );

            for (const cupom of cuponsParaAplicar) {
                await aplicarCupom(cupom);
            }

            for (const cupom of cuponsParaRemover) {
                if (cupom.codigo) {
                    await removerCupom(cupom.codigo, cupom.tipo);
                }
            }

            setCuponsSelecionados([]);
            fechar();
        } catch (error) {
            console.error("Erro ao aplicar/remover cupons:", error);
            setCuponsSelecionados([]);
            fechar();
        } finally {
            setLoadingConcluir(false);
        }
    };

    const handleCancelar = () => {
        setCuponsSelecionados([]);
        fechar();
    };

    const formatarValorCupom = (cupom: ICupom): string => {
        if (
            cupom.tipo === TipoCupom.PORCENTAGEM ||
            cupom.tipo === TipoCupom.PORCENTAGEM_PRODUTOS
        ) {
            return `${cupom.valor}%`;
        }
        return real(cupom.valor);
    };

    const valorDescontoValido =
        tipoDesconto === TipoCupom.PORCENTAGEM
            ? (parseFloat(valorDesconto) || 0) > 0
            : converterParaNumero(valorDesconto) > 0;

    return (
        <Drawer
            anchor="right"
            open={aberto}
            onClose={handleCancelar}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: "60vw" },
                    height: "100%",
                },
            }}
            sx={{ zIndex: 10000 }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        flexShrink: 0,
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography variant="h6">Cupons</Typography>
                    <IconButton onClick={handleCancelar} size="small">
                        <Close />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Seção 1: Desconto rápido */}
                    <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Aplicar desconto
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                            }}
                        >
                            <TextField
                                size="small"
                                label={tipoDesconto === TipoCupom.PORCENTAGEM ? "Porcentagem" : "Valor"}
                                value={valorDesconto}
                                onChange={handleValorChange}
                                placeholder={tipoDesconto === TipoCupom.PORCENTAGEM ? "0 - 100" : "0,00"}
                                type={tipoDesconto === TipoCupom.PORCENTAGEM ? "number" : "text"}
                                inputProps={{
                                    min: 0,
                                    max: tipoDesconto === TipoCupom.PORCENTAGEM ? 100 : undefined,
                                }}
                                InputProps={
                                    tipoDesconto === TipoCupom.TOTAL
                                        ? {
                                              startAdornment: (
                                                  <InputAdornment position="start">R$</InputAdornment>
                                              ),
                                          }
                                        : {
                                              endAdornment: (
                                                  <InputAdornment position="end">%</InputAdornment>
                                              ),
                                          }
                                }
                                sx={{ width: 160 }}
                            />

                            <FormControl component="fieldset">
                                <FormLabel component="legend" sx={{ fontSize: "0.75rem" }}>
                                    Tipo
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={tipoDesconto}
                                    onChange={(e) => {
                                        setTipoDesconto(e.target.value as TipoDescontoRapido);
                                        setValorDesconto("");
                                    }}
                                >
                                    <FormControlLabel
                                        value={TipoCupom.TOTAL}
                                        control={<Radio size="small" />}
                                        label="Valor fixo"
                                    />
                                    <FormControlLabel
                                        value={TipoCupom.PORCENTAGEM}
                                        control={<Radio size="small" />}
                                        label="Porcentagem"
                                    />
                                </RadioGroup>
                            </FormControl>

                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleAplicarDesconto}
                                disabled={!valorDescontoValido}
                                sx={{ alignSelf: "center", mt: 1 }}
                            >
                                Aplicar
                            </Button>
                        </Box>
                    </Box>

                    <Divider />

                    {/* Seção 2: Cupons pré-existentes */}
                    <Box sx={{ p: 2, flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Cupons disponíveis
                        </Typography>

                        {loading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: 120,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : todosCupons.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Nenhum cupom disponível
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" width={50}>
                                                Selecionar
                                            </TableCell>
                                            <TableCell>Código</TableCell>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>Tipo</TableCell>
                                            <TableCell align="right">Valor</TableCell>
                                            <TableCell align="right">Valor no Pedido</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {todosCupons.map((cupom, index) => {
                                            const isSelecionado = cuponsSelecionados.some(
                                                (c) =>
                                                    c.codigo === cupom.codigo &&
                                                    c.tipo === cupom.tipo
                                            );

                                            const ordemTipo: Record<string, number> = {
                                                [TipoCupom.TAXA_ENTREGA]: 1,
                                                [TipoCupom.PORCENTAGEM_PRODUTOS]: 2,
                                                [TipoCupom.TOTAL]: 3,
                                                [TipoCupom.PORCENTAGEM]: 4,
                                            };

                                            const ordemCupomAtual = ordemTipo[cupom.tipo] || 999;

                                            const cuponsParaCalculo = cuponsSelecionados
                                                .filter((c) => {
                                                    const ordemC = ordemTipo[c.tipo] || 999;
                                                    if (ordemC === ordemCupomAtual) {
                                                        const indexCupom = todosCupons.findIndex(
                                                            (cf) =>
                                                                cf.codigo === c.codigo &&
                                                                cf.tipo === c.tipo
                                                        );
                                                        return indexCupom < index;
                                                    }
                                                    return ordemC < ordemCupomAtual;
                                                })
                                                .sort((a, b) => {
                                                    const ordemA = ordemTipo[a.tipo] || 999;
                                                    const ordemB = ordemTipo[b.tipo] || 999;
                                                    if (ordemA !== ordemB) {
                                                        return ordemA - ordemB;
                                                    }
                                                    const indexA = todosCupons.findIndex(
                                                        (cf) =>
                                                            cf.codigo === a.codigo &&
                                                            cf.tipo === a.tipo
                                                    );
                                                    const indexB = todosCupons.findIndex(
                                                        (cf) =>
                                                            cf.codigo === b.codigo &&
                                                            cf.tipo === b.tipo
                                                    );
                                                    return indexA - indexB;
                                                });

                                            const desconto = calcularDescontoCupomComCuponsSelecionados(
                                                cupom,
                                                valorTotalProdutos,
                                                valorFrete,
                                                cuponsParaCalculo
                                            );

                                            return (
                                                <TableRow
                                                    key={`${cupom.codigo}-${cupom.tipo}-${index}`}
                                                    hover={!cupom.aplicado}
                                                    sx={{
                                                        backgroundColor: cupom.aplicado
                                                            ? "action.selected"
                                                            : "transparent",
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={isSelecionado}
                                                            onChange={() =>
                                                                toggleCupomSelecionado(cupom)
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>{cupom.codigo || "-"}</TableCell>
                                                    <TableCell>{cupom.nome || "-"}</TableCell>
                                                    <TableCell>
                                                        {TipoCupomDescricao[
                                                            cupom.tipo as keyof typeof TipoCupomDescricao
                                                        ]}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatarValorCupom(cupom)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {real(desconto)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        flexShrink: 0,
                    }}
                >
                    <Button variant="text" size="small" onClick={handleCancelar}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleConcluir}
                        disabled={!temMudancas || loadingConcluir}
                        startIcon={
                            loadingConcluir ? <CircularProgress size={16} /> : undefined
                        }
                    >
                        Concluir
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};
